'use client'

import { useState } from 'react'
import Image from 'next/image'
import { cn } from '@/lib/utils'
import { X, ChevronLeft, ChevronRight } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent } from '@/components/ui/dialog'

interface GalleryImage {
  url: string
  caption?: string
}

interface GalleryBlockContent {
  images: GalleryImage[]
}

interface GalleryBlockProps {
  content: GalleryBlockContent
  className?: string
}

export function GalleryBlock({ content, className }: GalleryBlockProps) {
  const [selectedIndex, setSelectedIndex] = useState<number | null>(null)

  if (!content.images?.length) return null

  const openLightbox = (index: number) => setSelectedIndex(index)
  const closeLightbox = () => setSelectedIndex(null)
  
  const goToNext = () => {
    if (selectedIndex === null) return
    setSelectedIndex((selectedIndex + 1) % content.images.length)
  }
  
  const goToPrev = () => {
    if (selectedIndex === null) return
    setSelectedIndex((selectedIndex - 1 + content.images.length) % content.images.length)
  }

  // Determine grid columns based on image count
  const getGridCols = () => {
    const count = content.images.length
    if (count === 1) return 'grid-cols-1'
    if (count === 2) return 'grid-cols-2'
    if (count <= 4) return 'grid-cols-2 sm:grid-cols-2'
    return 'grid-cols-2 sm:grid-cols-3'
  }

  return (
    <>
      <div className={cn('grid gap-2', getGridCols(), className)}>
        {content.images.map((image, index) => (
          <button
            key={image.url}
            onClick={() => openLightbox(index)}
            className="relative aspect-square overflow-hidden rounded-lg bg-zinc-800 group cursor-pointer focus:outline-none focus:ring-2 focus:ring-violet-500"
          >
            <Image
              src={image.url}
              alt={image.caption || `Gallery image ${index + 1}`}
              fill
              sizes="(max-width: 640px) 50vw, 33vw"
              className="object-cover transition-transform duration-300 group-hover:scale-105"
            />
            {image.caption && (
              <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/80 to-transparent p-2 opacity-0 group-hover:opacity-100 transition-opacity">
                <p className="text-xs text-white truncate">{image.caption}</p>
              </div>
            )}
          </button>
        ))}
      </div>

      {/* Lightbox */}
      <Dialog open={selectedIndex !== null} onOpenChange={() => closeLightbox()}>
        <DialogContent className="max-w-4xl w-full bg-black/95 border-zinc-800 p-0">
          <div className="relative">
            {/* Close button */}
            <Button
              variant="ghost"
              size="icon"
              onClick={closeLightbox}
              className="absolute top-2 right-2 z-10 text-white hover:bg-white/20"
              aria-label="Close lightbox"
            >
              <X className="h-5 w-5" />
            </Button>

            {/* Navigation buttons */}
            {content.images.length > 1 && (
              <>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={goToPrev}
                  className="absolute left-2 top-1/2 -translate-y-1/2 z-10 text-white hover:bg-white/20"
                  aria-label="Previous image"
                >
                  <ChevronLeft className="h-6 w-6" />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={goToNext}
                  className="absolute right-2 top-1/2 -translate-y-1/2 z-10 text-white hover:bg-white/20"
                  aria-label="Next image"
                >
                  <ChevronRight className="h-6 w-6" />
                </Button>
              </>
            )}

            {/* Image */}
            {selectedIndex !== null && (
              <div className="flex flex-col items-center">
                <div className="relative w-full h-[80vh]">
                  <Image
                    src={content.images[selectedIndex].url}
                    alt={content.images[selectedIndex].caption || `Gallery image ${selectedIndex + 1}`}
                    fill
                    sizes="(max-width: 768px) 100vw, 896px"
                    className="object-contain"
                  />
                </div>
                {content.images[selectedIndex].caption && (
                  <p className="text-white text-sm p-4 text-center">
                    {content.images[selectedIndex].caption}
                  </p>
                )}
                {/* Image counter */}
                <p className="text-zinc-500 text-xs pb-4">
                  {selectedIndex + 1} / {content.images.length}
                </p>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </>
  )
}



